/* eslint-disable no-underscore-dangle */
import React from 'react';
import PropTypes from 'prop-types';

const ReactDOM = require('react-dom');

const NOOP = require('lodash/noop');

const { container: getContainer, ownerDocument: OwnerDocument } = require('@ott/utility-overlay');

const relativeStyle = {
  position: 'relative',
  zIndex: 1,
};

class Portal extends React.Component {
  static INSTANCES = new Set();

  static propTypes = {
    // eslint-disable-next-line react/forbid-prop-types, react/no-unused-prop-types
    children: PropTypes.any,
    trigger: PropTypes.element,
    closeOnEsc: PropTypes.bool,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onOpen: PropTypes.func,
    // eslint-disable-next-line react/no-unused-prop-types
    zIndex: PropTypes.number,
    // eslint-disable-next-line react/no-unused-prop-types
    isStatic: PropTypes.bool,
  };

  static defaultProps = {
    closeOnEsc: true,
    onClose: NOOP,
    onOpen: NOOP,
    zIndex: 1,
    isStatic: false,
    // eslint-disable-next-line react/default-props-match-prop-types
    container: typeof document !== 'undefined' ? document.body : null,
  };

  constructor(props, ctx) {
    super(props, ctx);

    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleOpen = this.handleOpen.bind(this);

    this.state = {
      active: false,
    };
    this.node = null;
    this.portal = null;

    this._eventsBound = {
      close: false,
    };
  }

  // eslint-disable-next-line react/sort-comp
  shouldComponentUpdate() {
    return false;
  }

  componentDidMount() {
    this._mounted = true;

    setTimeout(() => {
      if (this.props.isOpen && !this.state.active) {
        this.handleOpen(this.props);
      }
    }, 0);
  }

  UNSAFE_componentWillReceiveProps(props) {
    const { active } = this.state;
    if (props.isOpen || (props.isOpen == null && active)) {
      this.handleOpen({ ...props, isOpen: true });
    } else if (active) {
      this.handleClose();
    }
  }

  componentWillUnmount() {
    this.handleClose();
    this._mounted = false;
  }

  handleTriggerClick() {
    setTimeout(() => this.handleOpen(this.props), 0);
  }

  handleKeyDown(event) {
    const insts = Portal.INSTANCES;
    if (insts.has(this.portal) && Array.from(insts.values()).pop() === this.portal) {
      if (event.keyCode === 27) {
        this.handleClose(true);
      }
    }
  }

  handleClose(closedByUser) {
    if (!this._closing) {
      this._closing = true;
      this._removePortal(closedByUser);
    }
  }

  handleOpen(props = this.props) {
    if (!this._mounted) {
      return;
    }

    this.setState(
      {
        active: true,
      },
      () => {
        if (!this._closing) {
          this.renderPortal(props);
          this.props.onOpen();
        }
      }
    );
  }

  checkFullScreen(props) {
    if (this.node) {
      if (props.isFullScreen) {
        this.node.style.height = '100%';
      } else {
        this.node.style.height = '';
      }
    }
  }

  _saveGetContainer() {
    const container = getContainer(this.props.container, OwnerDocument(this).body);

    if (!container || !container.appendChild) {
      // eslint-disable-next-line react/no-find-dom-node
      const node = ReactDOM.findDOMNode(container);
      if (node) {
        return node;
      }

      throw new Error('DOM node must be provided as container prop');
    }

    return container;
  }

  _appendPortalContainer() {
    if (!this.node) {
      const container = this._saveGetContainer();

      this.node = document.createElement('div');

      container.appendChild(this.node);

      if (this.props.closeOnEsc && !this._eventsBound.close) {
        this._eventsBound.close = true;
        document.addEventListener('keydown', this.handleKeyDown);
      }
    }
  }

  _removePortal(closedByUser) {
    if (!this.node) {
      return;
    }

    if (this._mounted) {
      this.setState({
        active: false,
      });
    }

    if (this.props.closeOnEsc && this._eventsBound.close) {
      this._eventsBound.close = false;
      document.removeEventListener('keydown', this.handleKeyDown);
    }

    this.props.onClose(closedByUser);

    Portal.INSTANCES.delete(this.portal);

    setTimeout(this._unmount.bind(this), 0);
  }

  _unmount() {
    if (this.node) {
      ReactDOM.unmountComponentAtNode(this.node);

      if (this.props.container) {
        this.props.container.removeChild(this.node);
      }
    }

    this.node = null;
    this.portal = null;
    this._closing = false;
  }

  renderPortal(props) {
    if (!props || !props.children) {
      return;
    }

    this._appendPortalContainer();

    if (this.portal) {
      Portal.INSTANCES.delete(this.portal);
    }

    this.checkFullScreen(props);

    const styles = props.isStatic ? {} : relativeStyle;

    // jscs:disable
    this.portal = ReactDOM.unstable_renderSubtreeIntoContainer(
      // jscs:enable
      this,
      <div
        style={{
          ...styles,
          ...this.props.style,
          zIndex: props.zIndex == null ? styles.zIndex : props.zIndex,
        }}
      >
        {React.cloneElement(props.children, {
          closePortal: this.handleClose,
          isOpen: this.state.active,
        })}
      </div>,
      this.node
    );

    Portal.INSTANCES.add(this.portal);
  }

  render() {
    const { trigger } = this.props;

    return trigger
      ? React.cloneElement(trigger, {
          ref: 'trigger',
          onClick: this.handleTriggerClick.bind(this, trigger.props.onClick),
        })
      : null;
  }
}

export default Portal;
